import type React from "react";
import { LucideIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/utils/shadcn";

interface StatCardProps {
  label: string;
  value: React.ReactNode;
  /** A short line under the value: a comparison, a share, a caveat. */
  hint?: React.ReactNode;
  icon?: LucideIcon;
  /** Emphasis for the one figure a row is about. */
  tone?: "default" | "primary" | "warning" | "danger";
  className?: string;
}

const TONE_CLASSES: Record<NonNullable<StatCardProps["tone"]>, string> = {
  default: "",
  primary: "border-chart-1/40 bg-chart-1/5",
  warning: "border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30",
  danger: "border-destructive/40 bg-destructive/5"
};

/** One headline figure. */
export function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  tone = "default",
  className
}: StatCardProps) {
  return (
    <Card className={cn(TONE_CLASSES[tone], className)}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-muted-foreground">{label}</p>
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        </div>
        <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">{value}</p>
        {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}
